import React, { useEffect, useState } from 'react';
import { ResponsiveModal } from '../../ui/ResponsiveModal';
import { CheckCircle, Download, FileCheck, FileText, Loader } from 'lucide-react';
import { dealsTranslations } from '../../../lib/i18n/translations/deals';
import type { DocumentFormat } from '../../../hooks/deals/usePDFGeneration';
import { QuoteLinesPicker, useQuoteLineSelection, type QuoteLine } from './QuoteLinesPicker';

interface TemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Template adatti ai servizi dell'offerta. */
  templates: any[];
  /** Non adatti: nascosti dietro "mostra tutti". */
  unsuitableTemplates?: any[];
  /** false = l'organizzazione vieta di usare template non adatti. */
  allowUnsuitable?: boolean;
  selectedTemplateId?: string;
  setSelectedTemplateId?: React.Dispatch<React.SetStateAction<string>>;
  /** `serviceIds`: righe dell'offerta scelte; assente = tutte */
  onGenerateDocument?: (format: DocumentFormat, serviceIds?: string[]) => Promise<void>;
  /** Servizi e prodotti dell'offerta, per scegliere cosa mettere nel documento */
  services?: QuoteLine[];
  /** @deprecated Use onGenerateDocument instead */
  onGeneratePDF?: () => Promise<void>;
  onGenerateSignatureLink?: () => Promise<void>;
  generatingPdf?: boolean;
  generatingSignatureLink?: boolean;
  debugMode?: boolean;
  translations?: any;
}

const TemplateModal: React.FC<TemplateModalProps> = ({
  isOpen,
  onClose,
  templates = [],
  unsuitableTemplates = [],
  allowUnsuitable = true,
  selectedTemplateId = "",
  setSelectedTemplateId = () => {},
  onGenerateDocument,
  onGeneratePDF = async () => {},
  onGenerateSignatureLink = async () => {},
  generatingPdf = false,
  generatingSignatureLink = false,
  debugMode = false,
  translations: t = dealsTranslations.it.templateModal,
  services = []
}) => {
  // State for document format toggle
  const [outputFormat, setOutputFormat] = useState<DocumentFormat>('pdf');

  // I template non adatti ai servizi dell'offerta restano nascosti finche' non
  // si chiede di vederli, e solo se l'organizzazione lo consente.
  const [mostraTuttiTemplate, setMostraTuttiTemplate] = useState(false);
  const nonAdattiVisibili = allowUnsuitable && mostraTuttiTemplate ? unsuitableTemplates : [];
  const templatesVisibili = [...templates, ...nonAdattiVisibili];

  // Servizi e prodotti da mettere nel documento: tutti a ogni apertura
  const lineSelection = useQuoteLineSelection(services, isOpen);

  useEffect(() => {
    if (!isOpen) setMostraTuttiTemplate(false);
  }, [isOpen]);

  // Auto-select first template when the modal is opened and no template is selected
  useEffect(() => {
    if (isOpen && templates.length > 0 && !selectedTemplateId) {
      setSelectedTemplateId(templates[0].id);
    }
  }, [isOpen, templates, selectedTemplateId, setSelectedTemplateId]);

  // Handle document generation with format
  const handleGenerateDocument = async () => {
    if (onGenerateDocument) {
      await onGenerateDocument(outputFormat, lineSelection.serviceIds);
    } else {
      // Fallback to legacy PDF-only function
      await onGeneratePDF();
    }
  };
  
  // Format toggle component for header - enabled for all environments
  const formatToggle = !debugMode ? (
    <div className="inline-flex p-0.5 bg-black/5 rounded-md">
      <button
        type="button"
        onClick={() => setOutputFormat('pdf')}
        className={`px-2.5 py-1 text-xs font-medium rounded transition-all duration-200 flex items-center ${
          outputFormat === 'pdf'
            ? 'bg-white text-black shadow-sm'
            : 'text-black/60 hover:text-black'
        }`}
      >
        <FileText className="h-3 w-3 mr-1" />
        PDF
      </button>
      <button
        type="button"
        onClick={() => setOutputFormat('docx')}
        className={`px-2.5 py-1 text-xs font-medium rounded transition-all duration-200 flex items-center ${
          outputFormat === 'docx'
            ? 'bg-white text-black shadow-sm'
            : 'text-black/60 hover:text-black'
        }`}
      >
        <FileText className="h-3 w-3 mr-1" />
        Word
      </button>
    </div>
  ) : null;

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title={t.title || "Select Template for Document"}
      size="lg"
      headerRight={formatToggle}
    >
      <div className="space-y-4">
        <p className="text-black/70">
          {t.description || 'Choose a template to generate a PDF document and create a signature link.'}
        </p>
        
        {templatesVisibili.length === 0 ? (
          <div className="py-4 text-center bg-gray-50 rounded-lg">
            <p className="text-black/60">
              {unsuitableTemplates.length > 0
                ? 'Nessun template adatto ai servizi di questa offerta. Modifica l\'ambito dei template nelle Impostazioni preventivi.'
                : (t.noTemplates || 'No templates available. Create a template in Settings first.')}
            </p>
          </div>
        ) : (
          <div className="max-h-64 overflow-y-auto">
            <div className="space-y-2">
              {templatesVisibili.map((template) => {
                const nonAdatto = nonAdattiVisibili.some((u) => u.id === template.id);
                return (
                <div
                  key={template.id}
                  className={`p-3 rounded-lg cursor-pointer border ${
                    selectedTemplateId === template.id
                      ? 'border-black bg-black/5'
                      : 'border-black/10 hover:bg-black/5'
                  }`}
                  onClick={() => setSelectedTemplateId(template.id)}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-medium">
                        {template.name}
                        {nonAdatto && (
                          <span className="ml-2 text-[11px] font-normal px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 align-middle">
                            non adatto ai servizi
                          </span>
                        )}
                      </h3>
                      {template.description && (
                        <p className="text-sm text-black/60">{template.description}</p>
                      )}
                    </div>
                    {selectedTemplateId === template.id && (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}

        {allowUnsuitable && unsuitableTemplates.length > 0 && (
          <button
            type="button"
            onClick={() => setMostraTuttiTemplate((v) => !v)}
            className="text-xs text-black/50 hover:text-black/80 underline"
          >
            {mostraTuttiTemplate
              ? 'Nascondi i template non adatti'
              : `Mostra tutti (${unsuitableTemplates.length} non adatti a questi servizi)`}
          </button>
        )}

        {!debugMode && (
          <QuoteLinesPicker
            lines={services}
            selected={lineSelection.selected}
            onChange={lineSelection.setSelected}
          />
        )}

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-black glass-card rounded-lg hover:bg-black/5"
          >
            {t.cancel || 'Cancel'}
          </button>

          {/* Conditional rendering of action buttons based on debug mode */}
          {debugMode ? (
            /* Preview HTML button - only shown in debug mode */
            <button
              type="button"
              onClick={onGeneratePDF}
              disabled={!selectedTemplateId || generatingPdf}
              className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-lg ${
                !selectedTemplateId || generatingPdf
                  ? 'bg-black/50 cursor-not-allowed'
                  : 'bg-black hover:bg-black/80'
              }`}
            >
              {generatingPdf ? (
                <>
                  <span className="mr-2">{t.generating || 'Generating...'}</span>
                  <span className="animate-spin">⏳</span>
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2 inline" />
                  Preview HTML
                </>
              )}
            </button>
          ) : (
            /* Document download and Signature buttons for normal mode */
            <>
              <button
                type="button"
                onClick={handleGenerateDocument}
                disabled={!selectedTemplateId || generatingPdf || lineSelection.noneSelected}
                className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-lg ${
                  !selectedTemplateId || generatingPdf || lineSelection.noneSelected
                    ? 'bg-black/50 cursor-not-allowed'
                    : 'bg-black hover:bg-black/80'
                }`}
              >
                {generatingPdf ? (
                  <>
                    <span className="mr-2">{t.generating || 'Generating...'}</span>
                    <Loader className="h-4 w-4 inline animate-spin" />
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2 inline" />
                    {outputFormat === 'docx'
                      ? (t.downloadWord || 'Download Word')
                      : (t.downloadPdf || 'Download PDF')
                    }
                  </>
                )}
              </button>

              {/* Temporaneamente nascosto
              <button
                type="button"
                onClick={onGenerateSignatureLink}
                disabled={!selectedTemplateId || generatingSignatureLink}
                className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-lg ${
                  !selectedTemplateId || generatingSignatureLink
                    ? 'bg-green-500/50 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {generatingSignatureLink ? (
                  <>
                    <span className="mr-2">{t.generating || 'Generating...'}</span>
                    <Loader className="h-4 w-4 inline animate-spin" />
                  </>
                ) : (
                  <>
                    <FileCheck className="h-4 w-4 mr-2 inline" />
                    {t.signature?.generateLink || "Generate Signature Link"}
                  </>
                )}
              </button>
              */}
            </>
          )}
        </div>
      </div>
    </ResponsiveModal>
  );
};

export default TemplateModal;